import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// A slideable product image gallery: arrows, dots, thumbnails and
// touch-swipe support. Pass an array of image URLs.
const ImageGallery = ({ images = [], alt = "" }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  const count = images.length;
  const go = (dir) =>
    setIndex((i) => (i + dir + count) % count);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  if (count === 0) return null;

  return (
    <div>
      <div
        className="relative rounded-3xl overflow-hidden gold-border select-none group"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${alt} ${i + 1}`}
              draggable={false}
              className="w-full h-[360px] md:h-[520px] object-cover shrink-0"
              style={{ flex: "0 0 100%" }}
            />
          ))}
        </div>

        {count > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-navy/70 backdrop-blur border border-gold/30 text-white flex items-center justify-center hover:bg-gold hover:text-navy transition-colors duration-200 opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-navy/70 backdrop-blur border border-gold/30 text-white flex items-center justify-center hover:bg-gold hover:text-navy transition-colors duration-200 opacity-0 group-hover:opacity-100"
            >
              <ChevronRight size={20} />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to image ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === index ? "w-7 bg-gold" : "w-2 bg-white/50 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="mt-4 flex gap-3">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-20 w-20 rounded-xl overflow-hidden border-2 transition-colors duration-200 ${
                i === index ? "border-gold" : "border-white/10 hover:border-gold/40"
              }`}
            >
              <img
                src={src}
                alt={`thumbnail ${i + 1}`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
